import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { CheckCircle2, XCircle, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InviteLoginDialog } from '@/components/auth/InviteLoginDialog'
import type { User } from '@supabase/supabase-js'
import mixpanel from 'mixpanel-browser'
import { applyAuthRedirect } from '@/lib/authRedirect'
import { stripExternalSessionParamsFromUrl } from '@/lib/sessionFixation'

type InviteOrgPreview = { name: string; logo_url: string | null }

function orgInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'
}

function InviteOrgAvatar({ name, logoUrl }: { name: string | null; logoUrl: string | null }) {
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    setImgFailed(false)
  }, [logoUrl])

  const initials = name ? orgInitials(name) : null
  const showImg = Boolean(logoUrl && !imgFailed)

  if (showImg) {
    return (
      <div className="flex h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
        <img
          src={logoUrl!}
          alt={name ? `Logo de ${name}` : 'Logo da organização'}
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      </div>
    )
  }

  if (initials) {
    return (
      <div
        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#eef2ff] text-lg font-semibold tracking-tight text-[#4f46e5]"
        aria-hidden
      >
        {initials}
      </div>
    )
  }

  return (
    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eef2ff]">
      <Users className="h-7 w-7 text-[#4f46e5]" aria-hidden />
    </div>
  )
}

async function fetchInviteOrgPreview(inviteId: string): Promise<InviteOrgPreview | null> {
  const { data, error } = await supabase.rpc('get_organization_invite_preview', {
    p_invite_id: inviteId,
  })
  if (error || data == null || typeof data !== 'object') return null
  const row = data as Record<string, unknown>
  const name = typeof row.name === 'string' ? row.name : null
  if (!name) return null
  const logo_url = typeof row.logo_url === 'string' ? row.logo_url : null
  return { name, logo_url }
}

// Função para fazer upload do avatar do Google para o storage
async function uploadGoogleAvatarToStorage(avatarUrl: string, userId: string): Promise<string | null> {
  try {
    const response = await fetch(avatarUrl)
    if (!response.ok) throw new Error('Erro ao baixar avatar do Google')

    const blob = await response.blob()
    const fileExt = avatarUrl.includes('=s') ? 'jpg' : avatarUrl.split('.').pop()?.split('?')[0] || 'jpg'
    const fileName = `avatar-${Date.now()}.${fileExt}`
    const filePath = `${userId}/${fileName}`

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, blob, { cacheControl: '3600', upsert: true })

    if (uploadError) {
      if (uploadError.message.includes('Bucket not found')) return avatarUrl
      return null
    }

    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath)
    return publicUrl
  } catch {
    return null
  }
}

async function processGoogleOAuthData(user: User) {
  try {
    const userMetadata = user.user_metadata || {}
    if (user.app_metadata?.provider !== 'google') return

    const fullName = userMetadata.full_name || userMetadata.name || user.email?.split('@')[0] || ''
    const googleAvatarUrl = userMetadata.avatar_url || userMetadata.picture
    let uploadedAvatarUrl: string | null = null
    if (googleAvatarUrl) uploadedAvatarUrl = await uploadGoogleAvatarToStorage(googleAvatarUrl, user.id)

    const profileUpdate: any = {
      id: user.id,
      name: fullName,
      email: user.email || user.user_metadata?.email || '',
      updated_at: new Date().toISOString(),
    }
    if (uploadedAvatarUrl) profileUpdate.avatar_url = uploadedAvatarUrl
    else if (googleAvatarUrl) profileUpdate.avatar_url = googleAvatarUrl

    await supabase.from('profiles').upsert(profileUpdate, { onConflict: 'id' })
  } catch (error) {
    console.error('Erro ao processar dados do Google OAuth:', error)
  }
}

function resolveOrgInviteId(searchParams: URLSearchParams): string | null {
  const direct = searchParams.get('org_invite_id') || searchParams.get('token')
  if (direct) return direct
  const next = searchParams.get('next')
  if (!next) return null
  try {
    const decoded = decodeURIComponent(next)
    const qIdx = decoded.indexOf('?')
    if (qIdx < 0) return null
    const q = new URLSearchParams(decoded.slice(qIdx + 1))
    return q.get('org_invite_id') || q.get('token')
  } catch {
    return null
  }
}

async function acceptOrganizationInviteFromQuery(searchParams: URLSearchParams) {
  const inviteId = resolveOrgInviteId(searchParams)
  if (!inviteId) return { handled: false, accepted: false }

  const { data, error } = await supabase.rpc('accept_organization_invite', {
    invite_id: inviteId,
  })

  if (error) {
    throw new Error(error.message || 'Não foi possível aceitar o convite da organização.')
  }

  if (!data) {
    throw new Error('Convite inválido, expirado ou já aceito.')
  }

  return { handled: true, accepted: true }
}

// ── Shared layout ─────────────────────────────────────────────────────────────

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#2a20ad] to-[#655bf1]">
      <div className="flex flex-col items-center gap-8 w-full max-w-[447px] px-4">
        {/* Logo */}
        <img
          src="/logos/logo-fluity-white.svg"
          alt="Fluity"
          className="h-10 w-auto object-contain"
        />

        {/* Card */}
        <div className="bg-white rounded-[24px] px-6 pt-10 pb-16 w-full flex flex-col gap-6 items-center animate-in fade-in slide-in-from-bottom-6 duration-700">
          {children}
        </div>
      </div>
    </div>
  )
}

// ── State cards ───────────────────────────────────────────────────────────────

function CardText({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col gap-1.5 items-center text-center w-[309px]">
      <p
        className="text-[#0f172a] text-2xl font-semibold leading-8 tracking-[-0.53px] w-full"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {title}
      </p>
      <p
        className="text-[#64748b] text-sm leading-5 w-[329px]"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {description}
      </p>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'invite_sign_in'>('loading')
  const [inviteLoginNext, setInviteLoginNext] = useState<string | null>(null)
  const [inviteOrgPreview, setInviteOrgPreview] = useState<InviteOrgPreview | null>(null)
  const [inviteLoginModalOpen, setInviteLoginModalOpen] = useState(false)
  const [isCrossDomain] = useState(() => {
    const hash = window.location.hash.replace(/^#/, '')
    return new URLSearchParams(hash).get('type') === 'cross_domain'
  })
  const [message, setMessage] = useState(() => {
    const hash = window.location.hash.replace(/^#/, '')
    return new URLSearchParams(hash).get('type') === 'cross_domain'
      ? 'Entrando no Fluity Chat...'
      : 'Aguarde enquanto verificamos sua confirmação'
  })

  // Captura os params no momento da montagem, antes do Supabase JS limpar a URL
  // após trocar o código PKCE pela sessão (replaceState remove toda a query string)
  const [setupMode] = useState(() => new URLSearchParams(window.location.search).get('setup') === '1')
  const [orgIdFromUrl] = useState(() => new URLSearchParams(window.location.search).get('org'))
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    handledRef.current = true

    const handleAuthCallback = async () => {
      try {
        // ── Cross-domain SSO: tokens chegam no hash do redirect do app principal ──
        const rawHash = window.location.hash.replace(/^#/, '')
        if (rawHash) {
          const hashParams = new URLSearchParams(rawHash)
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          const type = hashParams.get('type')

          if (type === 'cross_domain' && accessToken && refreshToken) {
            // Remover hash da URL antes de qualquer coisa
            window.history.replaceState(null, '', window.location.pathname + window.location.search)

            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })

            if (!sessionError) {
              const { data: { user } } = await supabase.auth.getUser()
              if (user) {
                mixpanel.identify(user.id)
                mixpanel.people.set({ $email: user.email, $name: user.user_metadata?.full_name || user.email })
                mixpanel.track('auth_login_success', { user_id: user.id, method: 'cross_domain' })
              }
              sessionStorage.setItem('fluity-post-login', 'cross_domain')
              navigate('/chat', { replace: true })
              return
            }

            setStatus('error')
            setMessage('Não foi possível iniciar sessão. Tente novamente.')
            setTimeout(() => navigate('/login', { replace: true }), 3000)
            return
          }
        }

        stripExternalSessionParamsFromUrl()

        const code = searchParams.get('code')
        const error = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')

        // Destino final: setup de senha para novos convidados, ou próxima rota solicitada
        const resolveDestination = (fallback: string) => {
          if (setupMode) {
            return `/complete-signup${orgIdFromUrl ? `?org=${orgIdFromUrl}` : ''}`
          }
          return fallback
        }

        const goToDestination = (fallback: string) => {
          applyAuthRedirect(navigate, resolveDestination(fallback), '/chat')
        }

        if (error) {
          setStatus('error')
          setMessage(errorDescription || 'Erro ao fazer login. Tente novamente.')
          toast({ title: 'Erro no login', description: errorDescription || 'Não foi possível fazer login.', variant: 'destructive' })
          setTimeout(() => navigate('/login', { replace: true }), 3000)
          return
        }

        if (code) {
          await new Promise(resolve => setTimeout(resolve, 1000))
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()

          if (sessionError) {
            setStatus('error')
            setMessage('Erro ao verificar sessão. Tente fazer login novamente.')
            toast({ title: 'Erro na verificação', description: sessionError.message, variant: 'destructive' })
            setTimeout(() => navigate('/login', { replace: true }), 3000)
            return
          }

          const activeSession = session ?? await (async () => {
            await new Promise(resolve => setTimeout(resolve, 2000))
            const { data: { session: s } } = await supabase.auth.getSession()
            return s
          })()

          if (activeSession?.user) {
            await processGoogleOAuthData(activeSession.user)
            const inviteResult = await acceptOrganizationInviteFromQuery(searchParams)
            mixpanel.identify(activeSession.user.id)
            mixpanel.people.set({
              $email: activeSession.user.email,
              $name: activeSession.user.user_metadata?.full_name || activeSession.user.email,
            })
            mixpanel.track('auth_login_success', {
              user_id: activeSession.user.id,
              method: 'google',
            })
            setStatus('success')
            setMessage('Iremos te redirecionar...')
            toast({
              title: inviteResult.handled ? 'Convite aceito!' : 'Login realizado!',
              description: inviteResult.handled
                ? 'Você agora faz parte da organização.'
                : 'Bem-vindo de volta!',
            })
            const next = searchParams.get('next') || '/chat'
            setTimeout(() => goToDestination(next), 1500)
          } else {
            setStatus('error')
            setMessage('Não foi possível completar o login. Tente novamente.')
            toast({ title: 'Erro no login', description: 'Não foi possível criar a sessão.', variant: 'destructive' })
            setTimeout(() => navigate('/login', { replace: true }), 3000)
          }
          return
        }

        const tokenHash = searchParams.get('token_hash')
        const type = searchParams.get('type') as 'email' | 'recovery' | 'invite' | null
        const next = searchParams.get('next') || '/chat'

        if (tokenHash && type) {
          const { data, error: otpError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type })

          if (otpError) {
            setStatus('error')
            setMessage('Link de confirmação inválido ou expirado. Por favor, solicite um novo e-mail de confirmação.')
            toast({ title: 'Erro na confirmação', description: otpError.message, variant: 'destructive' })
            setTimeout(() => navigate('/verify-email'), 3000)
            return
          }

          await new Promise(resolve => setTimeout(resolve, 500))
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user?.email_confirmed_at) {
            const inviteResult = await acceptOrganizationInviteFromQuery(searchParams)
            mixpanel.identify(session.user.id)
            mixpanel.people.set({
              $email: session.user.email,
              $name: session.user.user_metadata?.full_name || session.user.email,
            })
            mixpanel.track('auth_email_confirmed', {
              user_id: session.user.id,
              email: session.user.email,
            })
            setStatus('success')
            setMessage('Iremos te redirecionar...')
            toast({
              title: setupMode ? 'Conta criada!' : (inviteResult.handled ? 'Convite aceito!' : 'E-mail confirmado!'),
              description: setupMode
                ? 'Configure sua senha para acessar a plataforma.'
                : (inviteResult.handled ? 'Você agora faz parte da organização.' : 'Sua conta foi ativada com sucesso.'),
            })
            setTimeout(() => goToDestination(next), 1500)
          } else {
            setStatus('error')
            setMessage('E-mail ainda não foi confirmado. Verifique sua caixa de entrada.')
            setTimeout(() => navigate('/verify-email', { replace: true }), 3000)
          }
          return
        }

        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          const { data: { user: currentUser } } = await supabase.auth.getUser()
          if (currentUser?.email_confirmed_at) {
            const inviteResult = await acceptOrganizationInviteFromQuery(searchParams)
            mixpanel.identify(currentUser.id)
            mixpanel.people.set({
              $email: currentUser.email,
              $name: currentUser.user_metadata?.full_name || currentUser.email,
            })
            setStatus('success')
            setMessage('Iremos te redirecionar...')
            toast({
              title: setupMode ? 'Conta criada!' : (inviteResult.handled ? 'Convite aceito!' : 'E-mail confirmado!'),
              description: setupMode
                ? 'Configure sua senha para acessar a plataforma.'
                : (inviteResult.handled ? 'Você agora faz parte da organização.' : 'Sua conta foi ativada com sucesso.'),
            })
            setTimeout(() => goToDestination(next), 1500)
          } else {
            setStatus('error')
            setMessage('E-mail ainda não foi confirmado. Verifique sua caixa de entrada.')
            setTimeout(() => navigate('/verify-email', { replace: true }), 3000)
          }
          return
        }

        const inviteId = searchParams.get('org_invite_id') || searchParams.get('token')
        if (inviteId) {
          const preview = await fetchInviteOrgPreview(inviteId)
          setInviteOrgPreview(preview)
          const returnToAccept = `/accept-invite?org_invite_id=${inviteId}`
          setInviteLoginNext(encodeURIComponent(returnToAccept))
          setStatus('invite_sign_in')
          setMessage(
            preview
              ? `Você foi convidado para entrar na organização ${preview.name}. Faça login com o mesmo e-mail que recebeu o convite para aceitar.`
              : 'Você foi convidado para entrar em uma organização. Faça login com o mesmo e-mail que recebeu o convite para aceitar.'
          )
          return
        }

        setStatus('error')
        setMessage('Link de confirmação inválido ou já foi usado. Redirecionando para login...')
        toast({ title: 'Link inválido', description: 'Este link de confirmação não é válido.', variant: 'destructive' })
        setTimeout(() => navigate('/login', { replace: true }), 3000)

      } catch (error) {
        console.error('Erro inesperado no callback:', error)
        setStatus('error')
        setMessage('Ocorreu um erro inesperado. Por favor, tente novamente.')
        toast({ title: 'Erro inesperado', description: 'Tente novamente mais tarde.', variant: 'destructive' })
        setTimeout(() => navigate('/login', { replace: true }), 3000)
      }
    }

    handleAuthCallback()
  }, [searchParams, navigate, toast])

  if (status === 'loading' && isCrossDomain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-7 w-7 rounded-full border-[3px] border-muted border-t-primary animate-spin" />
      </div>
    )
  }

  if (status === 'loading') {
    return (
      <Layout>
        {/* Spinner */}
        <div className="h-8 w-8 rounded-full border-[3px] border-[#e0dbff] border-t-[#2563EB] animate-spin" />
        <CardText title="Verificando..." description={message} />
      </Layout>
    )
  }

  if (status === 'success') {
    return (
      <Layout>
        <CheckCircle2 className="h-8 w-8 text-green-500" />
        <CardText title="Sucesso!" description={message} />
      </Layout>
    )
  }

  if (status === 'invite_sign_in' && inviteLoginNext) {
    const signupHref = `/signup?next=${inviteLoginNext}`
    const inviteIdForModal = resolveOrgInviteId(searchParams) || ''
    const cardTitle = inviteOrgPreview?.name
      ? `Convite para ${inviteOrgPreview.name}`
      : 'Convite para a sua equipe'
    return (
      <Layout>
        <InviteOrgAvatar
          name={inviteOrgPreview?.name ?? null}
          logoUrl={inviteOrgPreview?.logo_url ?? null}
        />
        <CardText title={cardTitle} description={message} />
        <div className="flex w-full max-w-[309px] flex-col gap-3">
          <Button
            className="h-11 w-full rounded-xl bg-[#2563EB] text-base font-medium shadow-[0px_0px_24px_0px_rgba(37,99,235,0.35)] hover:bg-[#1d4ed8]"
            disabled={!inviteIdForModal}
            onClick={() => setInviteLoginModalOpen(true)}
          >
            Entrar na minha conta
          </Button>
          <Button
            variant="outline"
            className="h-11 w-full rounded-xl border-[#e2e8f0] text-base font-medium"
            onClick={() => navigate(signupHref, { replace: true })}
          >
            Criar conta
          </Button>
        </div>
        <p className="max-w-[309px] text-center text-xs leading-relaxed text-[#94a3b8]">
          Com e-mail e senha, o login abre neste cartão. Com Google, há um passo rápido na conta Google e você volta
          já logado. Em ambos os casos aceitamos o convite e levamos você para o app.
        </p>

        {inviteIdForModal ? (
          <InviteLoginDialog
            open={inviteLoginModalOpen}
            onOpenChange={setInviteLoginModalOpen}
            inviteId={inviteIdForModal}
            orgName={inviteOrgPreview?.name ?? null}
            onSuccess={() => {
              setStatus('success')
              setMessage('Iremos te redirecionar...')
              setTimeout(() => navigate('/chat', { replace: true }), 1500)
            }}
          />
        ) : null}
      </Layout>
    )
  }

  return (
    <Layout>
      <XCircle className="h-8 w-8 text-red-500" />
      <CardText title="Erro" description={message} />
    </Layout>
  )
}
